import React from 'react';
import { Container, Typography, Box, Divider, CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
    typography: {
        fontFamily: '"League Spartan", sans-serif', // Your local font
    },
});

function CookiePolicy() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Cookie Policy
                </Typography>
                <Divider />
                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Introduction
                    </Typography>
                    <Typography paragraph>
                        This Cookies Policy outlines how Kerdos Wallet ("we," "our," or "us") collects, utilizes, shares, and stores cookies in connection with its websites, <a rel="noreferrer" href="https://www.kerdosapp.io/" target='_blank'>https://www.kerdosapp.io/</a> (the "Sites"). This Policy is applicable to all Sites, applications, products, and services (collectively referred to as "Services") that are posted, linked, or referenced.
                    </Typography>
                    <Typography paragraph>
                        We value your privacy and are committed to being transparent about the technologies we employ. This Policy outlines the circumstances under which cookies are utilized on our sites, reflecting our commitment to transparency.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Can We Use Cookies?
                    </Typography>
                    <Typography paragraph>
                        Yes, we, along with our affiliates, marketing partners, and analytics providers, utilize web beacons or pixels to ensure that every visitor to the Sites has an optimal experience.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        What is a Cookie?
                    </Typography>
                    <Typography paragraph>
                        A cookie is a small text file stored on your computer's hard drive by a web server. Cookies can contain information that can be accessed later by the web server that issued the cookie. Some cookies are only used when you select specific features or preferences, while others are used continuously. You can review our current cookie list to learn more about each cookie. This list is updated frequently, so you may find additional cookies not mentioned here. Cookies, tags, and scripts can be embedded in websites and emails to deliver cookies, track visits, analyze usage, measure campaign effectiveness, and determine if an email was opened and acted upon. Service/analytics providers may use these technologies to generate reports on an individual or aggregated basis.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Why Do We Use Cookies?
                    </Typography>
                    <Typography paragraph>
                        The following are the main purposes of cookies:
                    </Typography>
                    <ul>
                        <li>To recognize returning or new customers</li>
                        <li>To allow you to register on our Sites and save your password</li>
                        <li>To enhance our sites and gain a better understanding of how you utilize our platforms</li>
                        <li>To deliver targeted or interest-based advertising</li>
                        <li>To track your browsing habits and behavior across various websites and other platforms</li>
                        <li>To gain deeper insights into the interests of our customers and visitors</li>
                    </ul>
                    <Typography paragraph>
                        Certain cookies are essential for the proper functioning of the Sites. These cookies enable us to provide you with the services you request and confirm that you have created an account and logged in to access content. Additionally, these cookies help us store your browsing history and enhance the security of our Sites.
                    </Typography>
                    <Typography paragraph>
                        For analysis and marketing purposes, we also utilize functional cookies and third-party cookies. Functional cookies ensure that certain features of the Sites operate correctly and help maintain your preferences. Analytics cookies, among others, gather information on how visitors interact with our Sites and which products and content they view most frequently. They also measure the effectiveness of third-party advertising. Advertising cookies are employed to target ads to the appropriate audiences and to ensure our ads appear in top search results. Cookies can be classified as either "session" cookies, which are automatically deleted when you close your browser, or "persistent" cookies, which remain until you delete them (as discussed below). You can find comprehensive details about all cookies used on the Sites in the Cookie Disclosure table.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Disabling Cookies
                    </Typography>
                    <Typography paragraph>
                        You can typically enable or disable cookies through a feature integrated into your browser. For more information on cookie settings in your specific browser, refer to the following links:
                    </Typography>
                    <ul>
                        <li>
                            To learn about "Private Browsing" and manage cookie settings in Firefox, click <a href="https://support.mozilla.org/en-US/kb/private-browsing-use-firefox-without-history">here</a>.
                        </li>
                        <li>
                            To understand Incognito mode and manage cookie settings in Chrome, click <a href="https://support.google.com/chrome/answer/95664">here</a>.
                        </li>
                        <li>
                            For information on InPrivate mode and how to manage cookie settings in Internet Explorer, click <a href="https://support.microsoft.com/en-us/help/278835/how-to-delete-cookie-files-in-internet-explorer">here</a>.
                        </li>
                        <li>
                            To explore Private Browsing and manage cookie settings in Safari, click <a href="https://support.apple.com/en-us/HT201265">here</a>.
                        </li>
                    </ul>
                    <Typography paragraph>
                        If you wish to learn more about cookies or how to control, disable, or delete them, please visit <a href="http://www.aboutcookies.org">aboutcookies.org</a> for comprehensive guidance. Additionally, third-party advertising networks like Google provide users with options to customize or opt out of specific preferences related to their internet browsing. Click <a href="https://adssettings.google.com/authenticated">here</a> to learn more about the features offered by Google.
                    </Typography>
                    <Typography paragraph>
                        Flash cookies may occasionally be utilized on our Sites. You can manage Flash cookies by visiting this link, as they cannot be controlled through your browser settings. Please be aware that some functionalities of the Site may not operate correctly if you choose to decline cookies. Additionally, we will be unable to provide you with personalized content or advertisements in such cases.
                    </Typography>
                    <Typography paragraph>
                        Cookies may also be used to link information from cookies with data we have about you. We will use this combined information as outlined in this Privacy Policy. Third parties that place cookies on our Sites may also associate your name and email address with additional information, which could include details about your past purchases, both online and offline, as well as your online behavior. If you are located in the European Economic Area, you have certain rights, including the right to correct, delete, or access the data we hold about you.
                    </Typography>
                </Box>

                <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                        Local Storage Policy
                    </Typography>
                    <Typography paragraph>
                        Local Storage refers to the JavaScript-enabled localStorage feature, which enables websites to store a small amount of data, such as numbers and letters, in your browser. This functionality is utilized in certain Services to assign you a unique user ID. Local Storage is deleted by the website that originally created it. Additionally, you can remove Local Storage from your browser at any time by accessing your web browser's settings.
                    </Typography>
                    <Typography paragraph>
                        <strong>How to Clear Local Storage from Your Browser</strong>
                    </Typography>
                    <ul>
                        <li>
                            <strong>Firefox:</strong> Local Storage is cleared in Firefox when the following conditions are met: (a) the user clears recent history; (b) cookies are selected for deletion; and (c) the time range is set to "Everything."
                        </li>
                        <li>
                            <strong>Chrome:</strong> You can clear local storage in Chrome by following specific steps.
                        </li>
                        <li>
                            <strong>Internet Explorer:</strong> To clear local storage in Internet Explorer, follow these steps:
                            <ul>
                                <li>Click on Tools and select Internet Options.</li>
                                <li>Go to the General tab.</li>
                                <li>Enable the option to delete browsing history upon exit.</li>
                                <li>Select "Cookies & website data" or "temporary web files and website files."</li>
                                <li>Uncheck "Preserve Favorites website information" at the top.</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Safari:</strong> To clear Local storage in Safari:
                            <ul>
                                <li>Click on Safari and select preferences.</li>
                                <li>Navigate to the Privacy tab.</li>
                                <li>Click on "Remove all site data."</li>
                                <li>Confirm by clicking "Delete Now."</li>
                            </ul>
                        </li>
                    </ul>
                </Box>

            </Container>
        </ThemeProvider>
    );
}

export default CookiePolicy;
